import React, { useEffect, useState } from 'react';
import {
    Box, Button, Chip, CircularProgress, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { ExpenseClaimService } from '../../services/expense-claim.service';
import { ExpensePolicyService } from '../../services/expense-policy.service';
import { useNavigate } from 'react-router-dom';
import {
    ExpenseClaimDTO,
    ExpensePolicyDTO,
} from '../../interfaces/expense.interface';
import { useToast } from '../../context/ToastContext';
import { useEmployeeNames } from '../../hooks/useEmployeeNames';
import { claimRef, claimStatusLabel, formatAmount, isFinalApprovalTier } from '../../utils/expenseDisplay';
import ExpenseClaimApprovalDialog from './ExpenseClaimApprovalDialog';

const PendingApprovalExpenseClaims: React.FC = () => {
    const [claims, setClaims] = useState<ExpenseClaimDTO[]>([]);
    const employeeName = useEmployeeNames(claims);
    const [policy, setPolicy] = useState<ExpensePolicyDTO | null>(null);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState<ExpenseClaimDTO | null>(null);
    const toast = useToast();
    const navigate = useNavigate();

    useEffect(() => {
        bootstrap();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const bootstrap = async () => {
        setLoading(true);
        try {
            const [pendingPage, pol] = await Promise.all([
                ExpenseClaimService.listPendingApproval(),
                ExpensePolicyService.getByInstitution().catch(() => null),
            ]);
            setClaims(pendingPage.content);
            setPolicy(pol);
        } catch (e: any) {
            toast.showApiError(e, 'Failed to load pending claims');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ p: 3 }} data-help-page="expense.pendingClaims.page">
            <Typography variant="h5" sx={{ mb: 2 }}>Pending Approval — Expense Claims</Typography>
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
            ) : (
                <TableContainer component={Paper} data-help-section="expense.pendingClaims.table">
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell data-help="expense.pendingClaims.col.claim">Claim</TableCell>
                                <TableCell data-help="expense.pendingClaims.col.title">Title</TableCell>
                                <TableCell data-help="expense.pendingClaims.col.employee">Employee</TableCell>
                                <TableCell data-help="expense.pendingClaims.col.period">Period</TableCell>
                                <TableCell align="center" data-help="expense.pendingClaims.col.lines">Lines</TableCell>
                                <TableCell align="right" data-help="expense.pendingClaims.col.total">Total</TableCell>
                                <TableCell data-help="expense.pendingClaims.col.tier">Tier</TableCell>
                                <TableCell data-help="expense.pendingClaims.col.status">Status</TableCell>
                                <TableCell align="right" data-help="expense.pendingClaims.col.action">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {claims.map(c => (
                                <TableRow
                                    key={c.id} hover
                                    onClick={() => navigate(`/expense/claims/${c.id}`)}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    <TableCell data-help="expense.pendingClaims.col.claim">{claimRef(c.id)}</TableCell>
                                    <TableCell data-help="expense.pendingClaims.col.title">{c.title}</TableCell>
                                    <TableCell data-help="expense.pendingClaims.col.employee">{employeeName(c)}</TableCell>
                                    <TableCell data-help="expense.pendingClaims.col.period">{c.periodFrom} → {c.periodTo}</TableCell>
                                    <TableCell align="center" data-help="expense.pendingClaims.col.lines">{c.lines?.length ?? 0}</TableCell>
                                    <TableCell align="right" data-help="expense.pendingClaims.col.total">{formatAmount(c.totalAmount)}</TableCell>
                                    <TableCell data-help="expense.pendingClaims.col.tier">{c.currentApprovalTier ?? 1}</TableCell>
                                    <TableCell data-help="expense.pendingClaims.col.status"><Chip size="small" label={claimStatusLabel(c.status)} /></TableCell>
                                    <TableCell align="right">
                                        <Button size="small" variant="contained" data-help="expense.pendingClaims.action.review" onClick={e => { e.stopPropagation(); setSelected(c); }}>
                                            Review
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {claims.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={9} align="center">
                                        <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                                            No claims await your approval.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            {selected && (
                <ExpenseClaimApprovalDialog
                    open={Boolean(selected)}
                    claim={selected}
                    isFinalTier={isFinalApprovalTier(policy, selected)}
                    onClose={() => setSelected(null)}
                    onResolved={() => { setSelected(null); bootstrap(); }}
                />
            )}
        </Box>
    );
};

export default PendingApprovalExpenseClaims;
